import {
    useLayoutEffect,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type ReactNode,
    type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { useAppDisplayModeContext } from '@archipro-design/aria';
import { cn } from '@rocco/utils/cn';
import { prefersReducedMotion } from '@rocco/utils/prefers-reduced-motion';

/**
 * The gates' card at rest, in rem: TwoPaneSurface's `md:max-w-244` by
 * `md:h-158.75`. A miniature is drawn at this size and scaled, so it is the
 * modal itself at a distance rather than a sketch of it.
 */
export const MODAL_WIDTH_REM = 61;
export const MODAL_HEIGHT_REM = 39.6875;
/** TwoPaneModal `corners="rounded"`: `md:rounded-5`. */
const MODAL_RADIUS_REM = 1.25;
/** The peek's own corner, as the other cards' peeks have it. */
const PEEK_RADIUS_REM = 0.75;

/** How long the miniature takes to become the modal. */
const FLIGHT_MS = 480;
const FLIGHT_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

const remPx = () =>
    typeof document === 'undefined'
        ? 16
        : parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;

/**
 * The two-pane card at its real size, scaled to fit whatever holds it. The
 * panes go in as children, laid out as TwoPaneSurface lays them out. Static:
 * nothing in a miniature is a control, because the card around it is.
 */
export const MiniModalFrame = ({
    scale,
    children,
}: {
    scale: number;
    children: ReactNode;
}) => (
    <div
        aria-hidden="true"
        className="absolute top-0 left-0 flex origin-top-left bg-white"
        style={{
            width: `${MODAL_WIDTH_REM}rem`,
            height: `${MODAL_HEIGHT_REM}rem`,
            transform: `scale(${scale})`,
            transition: `transform ${FLIGHT_MS}ms ${FLIGHT_EASING}`,
        }}
    >
        {children}
    </div>
);

/** TwoPaneSurface's left pane: inset, rounded, 478px wide. */
export const MINI_PANE_CLASS =
    'relative my-3.5 ml-3 w-119.5 shrink-0 overflow-hidden rounded-2.5';

/** TwoPaneSurface's right pane at its default padding. */
export const MINI_BODY_CLASS = 'flex min-h-0 flex-1 flex-col px-15.5 py-7';

/** The scale that fits the modal's rem width into the box. */
const useFitScale = (ref: RefObject<HTMLElement | null>) => {
    const [scale, setScale] = useState(0);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const fit = () =>
            setScale(el.clientWidth / (MODAL_WIDTH_REM * remPx()));
        fit();
        const observer = new ResizeObserver(fit);
        observer.observe(el);
        return () => observer.disconnect();
    }, [ref]);
    return scale;
};

/**
 * A card's hover: the modal it opens, small, rising out of the card the way
 * the website window and the product card rise out of theirs. Click the card
 * and it grows into the real thing — see {@link useGrowToModal}.
 *
 * Desktop only, and inert: there is no hover to open it on touch, and the card
 * underneath is the control.
 */
export const ModalPeek = ({
    boxRef,
    children,
}: {
    /** The peek's box, which the flight takes off from. */
    boxRef: RefObject<HTMLDivElement | null>;
    /** The panes, for {@link MiniModalFrame}. */
    children: ReactNode;
}) => {
    const scale = useFitScale(boxRef);
    return (
        <div className="pointer-events-none absolute inset-0 grid place-items-center max-md:hidden">
            <div
                ref={boxRef}
                className={cn(
                    'relative w-[52%] overflow-hidden bg-white',
                    'shadow-[0_1.5rem_3rem_rgba(0,0,0,0.28)]',
                    'translate-y-[175%] transition-transform duration-500',
                    '[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]',
                    'group-hover:translate-y-0',
                    'motion-reduce:transition-none',
                )}
                style={{
                    aspectRatio: `${MODAL_WIDTH_REM} / ${MODAL_HEIGHT_REM}`,
                    borderRadius: `${PEEK_RADIUS_REM}rem`,
                }}
            >
                {scale > 0 ? (
                    <MiniModalFrame scale={scale}>{children}</MiniModalFrame>
                ) : null}
            </div>
        </div>
    );
};

/**
 * The dialog's declared enter, with room to spare. The flight's mark must
 * outlive it: the stylesheet suppresses the enter by shrinking its duration
 * to nothing, and restoring the declared duration while less of it has
 * elapsed than it declares does not find a finished animation — it resumes
 * it mid-run, which replayed the zoom after the flight left.
 */
const UNMARK_DELAY_MS = 400;

let flightMarks = 0;
let unmarkTimer: number | undefined;

/**
 * Root markers for the stylesheet's rules, on two lifetimes. `-cover` lives
 * exactly as long as the flight and holds the dialog's overlay invisible
 * beneath the flight's own backdrop — it must lift the moment the flight's
 * backdrop leaves, or the page stands undarkened behind the modal.
 * `data-modal-flight` outlives the flight by {@link UNMARK_DELAY_MS} and
 * keeps the enter animations' durations at nothing until their declared
 * time has truly elapsed.
 */
const markFlight = () => {
    flightMarks += 1;
    if (unmarkTimer !== undefined) {
        window.clearTimeout(unmarkTimer);
        unmarkTimer = undefined;
    }
    document.documentElement.setAttribute('data-modal-flight', '');
    document.documentElement.setAttribute('data-modal-flight-cover', '');
};

const unmarkFlight = () => {
    flightMarks -= 1;
    if (flightMarks > 0) return;
    document.documentElement.removeAttribute('data-modal-flight-cover');
    unmarkTimer = window.setTimeout(() => {
        document.documentElement.removeAttribute('data-modal-flight');
    }, UNMARK_DELAY_MS);
};

interface Flight {
    from: { left: number; top: number; width: number; height: number };
    mini: ReactNode;
    onArrive: () => void;
}

/**
 * Where the dialog will sit: centred, at the surface's size or the
 * viewport's. The layout viewport (clientWidth), not innerWidth: the dialog
 * is fixed-centred inside the viewport less the reserved scrollbar gutter,
 * and innerWidth counts the scrollbar — centring on it landed the flight
 * half a gutter to the right of where the dialog opens.
 */
const modalRect = () => {
    const rem = remPx();
    const viewportWidth = document.documentElement.clientWidth;
    const width = Math.min(MODAL_WIDTH_REM * rem, viewportWidth);
    const height = MODAL_HEIGHT_REM * rem;
    return {
        left: (viewportWidth - width) / 2,
        top: (window.innerHeight - height) / 2,
        width,
        height,
    };
};

/**
 * The miniature on its way to being the modal: a fixed copy that starts over
 * the peek and travels to where the dialog opens, growing as it goes. The
 * gate opens as it lands, and the copy stays under the dialog's fade-in so
 * the modal is already there when the real one paints.
 */
const ModalFlight = ({
    flight,
    onDone,
}: {
    flight: Flight;
    onDone: () => void;
}) => {
    const [landed, setLanded] = useState(false);
    const arrived = useRef(false);
    const ref = useRef<HTMLDivElement>(null);
    const rem = remPx();
    const to = modalRect();
    const box = landed ? to : flight.from;
    const scale = box.width / (MODAL_WIDTH_REM * rem);

    // Take off on the render after the first: reading the box's layout
    // commits the starting position, so the move to the destination is a
    // transition from it rather than a first paint already there.
    useLayoutEffect(() => {
        ref.current?.getBoundingClientRect();
        setLanded(true);
    }, []);

    // The flag the stylesheet reads (see tailwind.css): while a flight is
    // up, the dialog it opens skips its own reveal — the overlay stays
    // invisible under this copy's backdrop, and the pane mounts at full
    // size instead of replaying the arrival, small, over a card that has
    // already arrived. An attribute plus the compiled stylesheet, not a
    // <style> in this portal: the pane's enter kept running under the
    // portal-rendered rule.
    useLayoutEffect(() => {
        markFlight();
        return unmarkFlight;
    }, []);

    useEffect(() => {
        const arrive = window.setTimeout(() => {
            arrived.current = true;
            flight.onArrive();
        }, FLIGHT_MS);

        // Leave once the dialog's pane has painted. The pane's portal mounts
        // after this one and paints over the copy, at full size from its
        // first frame — the rules above run its enter out at mount — so from
        // then on the reader is looking at the real modal and the copy has
        // nothing left to cover. Watched rather than counted, because a beat
        // of state (and on a cold cache, a lazy chunk) sits between the
        // landing and the pane appearing; the deadline stands in for a
        // dialog that never comes.
        let raf = 0;
        let paneFrames = 0;
        const deadline = performance.now() + FLIGHT_MS + 4000;
        const leaveWhenPainted = () => {
            if (document.querySelector('.rocco-dialog-content')) {
                paneFrames += 1;
            }
            if (
                (arrived.current && paneFrames >= 2) ||
                performance.now() > deadline
            ) {
                onDone();
                return;
            }
            raf = requestAnimationFrame(leaveWhenPainted);
        };
        raf = requestAnimationFrame(leaveWhenPainted);

        return () => {
            window.clearTimeout(arrive);
            cancelAnimationFrame(raf);
            if (!arrived.current) flight.onArrive();
        };
        // The flight is fixed at take-off.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const style: CSSProperties = {
        left: box.left,
        top: box.top,
        width: box.width,
        height: box.height,
        borderRadius: `${landed ? MODAL_RADIUS_REM : PEEK_RADIUS_REM}rem`,
        transition: `left ${FLIGHT_MS}ms ${FLIGHT_EASING}, top ${FLIGHT_MS}ms ${FLIGHT_EASING}, width ${FLIGHT_MS}ms ${FLIGHT_EASING}, height ${FLIGHT_MS}ms ${FLIGHT_EASING}, border-radius ${FLIGHT_MS}ms ${FLIGHT_EASING}`,
    };

    return createPortal(
        <>
            {/* The dialog's own backdrop, brought in with the flight rather
                than popping at the landing: it darkens as the miniature
                travels and stands as THE backdrop until the flight unmounts.
                The dialog this flight opens skips its own reveal meanwhile —
                the html[data-modal-flight] rules in tailwind.css, set by the
                effect above, hold its overlay invisible (two translucent
                blacks painted together stack to a deeper one, which read as
                a flash) and run its pane's enter out at mount, so the pane
                paints settled and the overlay appears already at rest when
                the attribute lifts. */}
            <div
                aria-hidden="true"
                className={cn(
                    // Takes clicks, as the dialog backdrop it stands in for
                    // does. Left click-through, the page under a flight
                    // stayed live for the whole 480ms: a second gate on the
                    // same section (the hire block mounts two) could be
                    // pressed mid-travel and stack its own flight and dialog
                    // on the first. The miniature above keeps
                    // pointer-events-none — it is scenery, not a surface.
                    'fixed inset-0 z-modalBackdrop bg-black/80',
                    landed ? 'opacity-100' : 'opacity-0',
                )}
                style={{
                    transition: `opacity ${FLIGHT_MS}ms ${FLIGHT_EASING}`,
                }}
            />
            <div
                ref={ref}
                aria-hidden="true"
                data-testid="modal-flight"
                className="pointer-events-none fixed z-modal overflow-hidden bg-white shadow-[0_1.5rem_3rem_rgba(0,0,0,0.28)]"
                style={style}
            >
                <MiniModalFrame scale={scale}>{flight.mini}</MiniModalFrame>
            </div>
        </>,
        document.body,
    );
};

/**
 * Opening a modal from its card by growing the peek into it.
 *
 * `grow` takes off from where the peek rests — the card's centre, whether or
 * not the pointer has brought it up — and calls `open` as it lands. Where
 * motion is reduced, or there is no peek to grow, it opens at once.
 */
export const useGrowToModal = (
    boxRef: RefObject<HTMLDivElement | null>,
    mini: ReactNode,
) => {
    const [flight, setFlight] = useState<Flight | null>(null);
    // UA-derived, like the surfaces themselves: on the phone every gate
    // opens as a bottom sheet, so a flight to the desktop card's centred
    // rect animates towards geometry that never arrives — the miniature
    // hangs mid-screen while the sheet rises under it. The click opens the
    // sheet directly, with the sheet's own entrance.
    const { mobile } = useAppDisplayModeContext();

    const grow = (open: () => void) => {
        const box = boxRef.current;
        const stage = box?.parentElement;
        if (mobile || !box || !stage || prefersReducedMotion() || flight) {
            open();
            return;
        }
        const stageRect = stage.getBoundingClientRect();
        const width = box.clientWidth;
        const height = box.clientHeight;
        setFlight({
            from: {
                left: stageRect.left + (stageRect.width - width) / 2,
                top: stageRect.top + (stageRect.height - height) / 2,
                width,
                height,
            },
            mini,
            onArrive: open,
        });
    };

    const element: ReactNode = flight ? (
        <ModalFlight flight={flight} onDone={() => setFlight(null)} />
    ) : null;

    return { grow, element };
};
